import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Download, Send } from "lucide-react";
import { ApiError } from "@finora/api-client";
import { Button, EmptyState, Skeleton } from "@finora/ui";
import { formatMoney } from "@finora/config";
import { api } from "../../lib/api";

function today(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function downloadXlsxBase64(base64: string, fileName: string) {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const blob = new Blob([bytes], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

const METHOD_LABEL: Record<string, string> = {
  CASH: "Gotovina",
  CARD: "Kartica",
  WIRE_TRANSFER: "Virman",
  VOUCHER: "Vaučer",
  CHECK: "Ček",
  OTHER: "Ostalo",
};

export function DailyKasaReportTab() {
  const qc = useQueryClient();
  const [date, setDate] = useState(today());
  const [message, setMessage] = useState<string | null>(null);
  const [autoEnabled, setAutoEnabled] = useState(true);
  const [autoHour, setAutoHour] = useState(3);

  const { data: status } = useQuery({
    queryKey: ["minimax-status"],
    queryFn: () => api.getMinimaxStatus(),
    retry: false,
  });
  const minimaxReady = !!status?.connected;

  useEffect(() => {
    if (status) {
      setAutoEnabled(status.autoSendKasaEnabled);
      setAutoHour(status.autoSendKasaHour);
    }
  }, [status]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["daily-kasa", date],
    queryFn: () => api.getDailyKasaReport(date),
  });

  const exportMut = useMutation({
    mutationFn: () => api.getDailyKasaXlsx(date),
    onSuccess: (r) => downloadXlsxBase64(r.base64, r.fileName),
    onError: (e) => setMessage(e instanceof ApiError ? e.message : "Izvoz nije uspeo."),
  });

  const sendMut = useMutation({
    mutationFn: async () => {
      const stock = await api.sendMinimaxDailyReport(date, "KASA");
      const posting = await api.sendMinimaxDailyInvoicePosting(date, "KASA");
      return { stock, posting };
    },
    onSuccess: () => {
      setMessage(`Izveštaj za ${date} poslat u Minimax.`);
      qc.invalidateQueries({ queryKey: ["minimax-reports"] });
    },
    onError: (e) => setMessage(e instanceof ApiError ? e.message : "Slanje u Minimax nije uspelo."),
  });

  const autoMut = useMutation({
    mutationFn: () => api.saveMinimaxAutoSend(autoEnabled, autoHour, "KASA"),
    onSuccess: () => {
      setMessage("Podešavanje automatskog slanja sačuvano.");
      qc.invalidateQueries({ queryKey: ["minimax-status"] });
    },
    onError: (e) => setMessage(e instanceof ApiError ? e.message : "Čuvanje nije uspelo."),
  });

  const devices: any[] = data?.devices ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <label className="text-sm">
          <span className="mb-1 block text-slate-500">Datum</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="rounded-lg border border-border px-3 py-1.5 text-sm"
          />
        </label>
        <Button variant="secondary" onClick={() => exportMut.mutate()} disabled={exportMut.isPending || devices.length === 0}>
          <Download className="mr-1 h-4 w-4" /> Izvezi u Excel
        </Button>
        <Button
          onClick={() => sendMut.mutate()}
          disabled={!minimaxReady || sendMut.isPending || devices.length === 0}
          title={minimaxReady ? "" : "Minimax nije povezan"}
        >
          <Send className="mr-1 h-4 w-4" /> Pošalji u Minimax
        </Button>
      </div>

      {message && <div className="rounded-lg border border-border bg-surface px-4 py-2 text-sm">{message}</div>}

      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : isError ? (
        <EmptyState icon={AlertTriangle} title="Izveštaj nije učitan" description="Pokušaj ponovo." />
      ) : devices.length === 0 ? (
        <EmptyState icon={AlertTriangle} title="Nema računa sa kase" description={`Za ${date} nema računa izdatih sa naše kase.`} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              ["Prodaja", `${data.totals.salesCount} rač. / ${formatMoney(data.totals.salesGross)}`],
              ["Storno", `${data.totals.refundsCount} rač. / ${formatMoney(data.totals.refundsGross)}`],
              ["Neto promet", formatMoney(data.totals.netGross)],
              ["Neto PDV", formatMoney(data.totals.netTax)],
            ].map(([k, v]) => (
              <div key={k} className="rounded-xl border border-border bg-surface p-3 shadow-subtle">
                <div className="text-xs text-slate-500">{k}</div>
                <div className="text-base font-semibold text-slate-900">{v}</div>
              </div>
            ))}
          </div>

          {devices.map((d, i) => (
            <div key={i} className="space-y-3 rounded-xl border border-border bg-surface p-4 shadow-subtle">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="font-semibold text-slate-900">
                  {d.device?.name || "Kasa"} {d.businessLocation?.name ? `· ${d.businessLocation.name}` : ""}
                </h3>
                <span className="text-xs text-slate-500">
                  Računi: {d.fiscalNumbers?.first ?? "-"} → {d.fiscalNumbers?.last ?? "-"}
                </span>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500">
                      <th>Plaćanje</th>
                      <th className="text-right">Prodaja</th>
                      <th className="text-right">Storno</th>
                      <th className="text-right">Neto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.payments.map((p: any) => (
                      <tr key={p.method}>
                        <td>{METHOD_LABEL[p.method] ?? p.method}</td>
                        <td className="text-right">{formatMoney(p.sales)}</td>
                        <td className="text-right">{formatMoney(p.refunds)}</td>
                        <td className="text-right">{formatMoney(p.net)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500">
                      <th>PDV</th>
                      <th className="text-right">Osnovica</th>
                      <th className="text-right">PDV</th>
                      <th className="text-right">Bruto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.taxes.map((t: any) => (
                      <tr key={t.label}>
                        <td>{t.label}</td>
                        <td className="text-right">{formatMoney(t.base)}</td>
                        <td className="text-right">{formatMoney(t.tax)}</td>
                        <td className="text-right">{formatMoney(t.gross)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </>
      )}

      {minimaxReady && (
        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-surface p-4 shadow-subtle">
          <div className="w-full text-sm font-medium text-slate-900">Automatsko slanje računa sa kase u Minimax</div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={autoEnabled} onChange={(e) => setAutoEnabled(e.target.checked)} />
            Šalji automatski svaki dan
          </label>
          <label className="text-sm">
            <span className="mr-2 text-slate-500">u</span>
            <select
              value={autoHour}
              onChange={(e) => setAutoHour(Number(e.target.value))}
              className="rounded-lg border border-border px-2 py-1.5 text-sm"
              disabled={!autoEnabled}
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>
                  {String(h).padStart(2, "0")}:00
                </option>
              ))}
            </select>
          </label>
          <Button variant="secondary" onClick={() => autoMut.mutate()} disabled={autoMut.isPending}>
            Sačuvaj
          </Button>
          <p className="w-full text-xs text-slate-500">
            Ako je sat pre 12:00, šalje se izveštaj za prethodni dan; od 12:00 nadalje za tekući dan.
          </p>
        </div>
      )}
    </div>
  );
}
